import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function OrdersTable({
  rows = [],
  sort = { field: "date", direction: "desc" },
  onSortChange = () => {},
  isLoading = false,
  onClear,
  currency = "INR",
}) {
  const navigate = useNavigate();
  const intl = useMemo(
    () => new Intl.NumberFormat(undefined, { style: "currency", currency }),
    [currency]
  );

  const toggleSort = (field) => {
    const nextDir =
      sort.field === field
        ? sort.direction === "asc"
          ? "desc"
          : "asc"
        : "desc";
    onSortChange({ field, direction: nextDir });
  };

  const thClass = (field) =>
    "ot-th" +
    (field ? " is-sortable" : "") +
    (sort.field === field ? ` is-active ${sort.direction}` : "");

  return (
    <div className="orders-table">
      <table className="ot-table">
        <thead className="ot-head">
          <tr>
            <th className="ot-th">Order ID</th>

            <th className="ot-th">Customer</th>

            <th
              className={thClass("date")}
              onClick={() => toggleSort("date")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleSort("date")}
            >
              <span>Date</span>
              <SortCaret active={sort.field === "date"} dir={sort.direction} />
            </th>

            <th className="ot-th">Order Type</th>
            <th className="ot-th">Table Number</th>

            <th
              className={thClass("amount") + " is-numeric"}
              onClick={() => toggleSort("amount")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleSort("amount")}
            >
              <span>Amount</span>
              <SortCaret
                active={sort.field === "amount"}
                dir={sort.direction}
              />
            </th>

            <th className="ot-th">Status</th>
          </tr>
        </thead>

        <tbody className="ot-body">
          {isLoading ? (
            Array.from({ length: 10 }).map((_, i) => (
              <SkeletonRow key={`sk-${i}`} />
            ))
          ) : rows.length > 0 ? (
            rows.map((r) => (
              <tr className="ot-row" key={r.id}>
                <td
                  className="ot-cell id"
                  onClick={() => navigate(`/orders/l2/${r.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  {r.id}
                </td>

                <td className="ot-cell customer">{r.customer}</td>

                <td className="ot-cell date">
                  <DateCell iso={r.date} />
                </td>

                <td className="ot-cell type">
                  <TypeChip type={r.order_type} />
                </td>

                <td className="ot-cell table-cell">{r.table_number ?? "—"}</td>

                <td className="ot-cell amount is-numeric">
                  {typeof r.amount === "number"
                    ? currency === "INR"
                      ? `₹${r.amount.toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}`
                      : intl.format(r.amount)
                    : "—"}
                </td>

                <td className="ot-cell status">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))
          ) : (
            <tr className="ot-row-empty">
              <td className="ot-empty" colSpan={8}>
                <div className="ot-empty-box">
                  <div className="ot-empty-title">No data available</div>
                  {onClear ? (
                    <button className="ot-empty-btn" onClick={onClear}>
                      Clear filters
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DateCell({ iso }) {
  const d = iso ? new Date(iso) : null;
  if (!d) return "—";
  const date = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div className="date-cell">
      <div className="date-line">{date}</div>
      <div className="time-line">{time}</div>
    </div>
  );
}

function TypeChip({ type }) {
  const t = (type || "").toUpperCase();
  const map = {
    DINE_IN: { label: "Dine-In", className: "type--dinein" },
    TAKEAWAY: { label: "Takeaway", className: "type--takeaway" },
    ONLINE: { label: "Online", className: "type--online" },
  };
  const info = map[t] || { label: type || "—", className: "" };
  return (
    <span className={`chip ${info.className}`}>
      <span className="dot" />
      {info.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const map = {
    COMPLETED: { label: "Paid", className: "status--completed" },
    ON_PROCESS: { label: "On Process", className: "status--onprocess" },
    CANCELED: { label: "Canceled", className: "status--canceled" },
  };
  const info = map[s] || { label: status || "—", className: "" };
  return <span className={`badge ${info.className}`}>{info.label}</span>;
}

function SortCaret({ active, dir }) {
  return (
    <span className={"sort-caret" + (active ? ` ${dir}` : "")} aria-hidden>
      ▴▾
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="ot-row sk">
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} className="ot-cell">
          <span className="sk-bar" />
        </td>
      ))}
    </tr>
  );
}
